import { Component, ElementRef, Input, QueryList, Renderer2, ViewChildren } from '@angular/core';
import { INDISpecial } from '../indi-special';
import { NullsHandling, SortAccessorMap, SortState, sortItems } from 'src/app/utils/sort-utils';

type INDISortKey =
  | 'sku'
  | 'description'
  | 'class2'
  | 'indiSupplierName'
  | 'supplierSku'
  | 'orderUnit'
  | 'supplierPrice'
  | 'salesPrice'
  | 'margin'
  | 'creationBy'
  | 'creationDate';

@Component({
  selector: 'app-indi-specials-table',
  templateUrl: './indi-specials-table.component.html',
  styleUrls: ['./indi-specials-table.component.scss'],
})
export class INDISpecialsTableComponent {
  @ViewChildren('sortIcons') sortIcons!: QueryList<ElementRef>;
  @Input() specials: INDISpecial[] = [];
  selectedIds = new Set<number>();
  selectedSpecials: INDISpecial[] = [];

  page: number = 1;
  searchText: string = '';
  sortState: SortState<INDISortKey> | null = null;
  sortAccessors: SortAccessorMap<INDISpecial, INDISortKey> = {
    sku: special => special.sku,
    description: special => special.description,
    class2: special => {
      const class2 = special.class2;
      return class2 ? class2.trim() || null : null;
    },
    indiSupplierName: special => special.indiSupplierName || null,
    supplierSku: special => special.supplierSKU,
    orderUnit: special => special.orderUnit,
    supplierPrice: special => special.supplierPrice,
    salesPrice: special => special.salesPrice,
    margin: special => special.margin,
    creationBy: special => special.creationBy,
    creationDate: special => special.creationDate,
  };
  sortNullsHandling: Partial<Record<INDISortKey, NullsHandling>> = {
    class2: 'last',
    indiSupplierName: 'last',
  };

  constructor(private renderer: Renderer2) {}

  isSelected(s: INDISpecial): boolean {
    return this.selectedIds.has(s.id);
  }

  toggleSelection(special: INDISpecial, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedIds.add(special.id);
      this.selectedSpecials.push(special);
    } else {
      this.selectedIds.delete(special.id);
      this.selectedSpecials = this.selectedSpecials.filter(s => s.id !== special.id);
    }
  }

  getNextAscending(field: INDISortKey): boolean {
    if (!this.sortState || this.sortState.key !== field) {
      return true;
    }

    return !this.sortState.ascending;
  }

  sortByField(field: INDISortKey, ascendingOrder: boolean = this.getNextAscending(field)) {
    this.sortState = { key: field, ascending: ascendingOrder };
    const nullsHandling = this.sortNullsHandling[field] ?? 'equal';
    this.specials = sortItems(this.specials, field, ascendingOrder, this.sortAccessors, nullsHandling);
    this.updateSortingHighlight(`${field}${ascendingOrder ? 'Asc' : 'Desc'}`);
  }

  updateSortingHighlight(highlightElement: string) {
    this.sortIcons.forEach((element: ElementRef) => {
      const nativeElement = element.nativeElement as HTMLElement;
      if (nativeElement.id == highlightElement) {
        this.renderer.addClass(nativeElement, 'highlight');
      } else {
        this.renderer.removeClass(nativeElement, 'highlight');
      }
    });
  }

  onSearchTextChange() {
    this.page = 1;
  }
}
